import * as React from 'react';
import { Box, Button, Stack, TablePagination } from '@mui/material';
import colors from 'assets/theme/base/colors';
import borders from 'assets/theme/base/borders';
import FlexibleTable from 'component/flexible-table';
import { useEffect, useState } from 'react';
import { RowDataType, TableMetaData } from 'types/common/common';
import { useAppDispatch } from 'hooks/redux';
import { openConfirmationDialogs } from 'store/slices/confirmation-dialog/confirmation-dialog-slice';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import pxToRem from 'assets/theme/function/px-to-rem';
import { getStatusColor } from 'component/status-style';
import { StyledTableRow } from 'component/tabel-pagination-style';
import {
    NotificationDetailsEdit,
    NotificationSystemListRequest,
} from 'types/notification/notification';
import { useNavigate } from 'react-router-dom';
import Filter from 'component/filter';
import FilterNotificationSystem from 'pages/notification/notification-filter';
import { useLazyNotificationListQuery } from 'store/slices/notification/notification-list';
import { useNotificationActivateMutation } from 'store/slices/notification/notification';

export const initialListData: NotificationSystemListRequest = {
    page: 1,
    pageSize: 10,
    sort: 'createdAt',
};

const defaultTableStaffDataList: TableMetaData[] = [
    {
        key: 'notificationCreationType',
        position: 1,
        title: 'Creation Type',
        isSortable: false,
    },
    {
        key: 'notificationGroup',
        position: 2,
        title: 'Group',
        isSortable: false,
    },
    {
        key: 'iconName',
        position: 3,
        title: 'Icon Name',
        isSortable: false,
    },
    {
        key: 'notificationType',
        position: 4,
        title: 'Type',
        isSortable: false,
    },

    {
        key: 'status',
        position: 5,
        title: 'Status',
        isSortable: false,
    },
    {
        key: 'createdAt',
        position: 6,
        title: 'Creation Data',
        isSortable: false,
        type: 'date',
        format: 'DD.MM.YYYY HH:mm',
    },
];

const Notification: React.FC = () => {
    const [tableMetaDataList, setTableMetaDataList] = useState<TableMetaData[]>(
        defaultTableStaffDataList,
    );
    const [dataNotificationRequestList, setDataNotificationRequestList] =
        React.useState<NotificationSystemListRequest>(initialListData);
    const [pagesCount, setPagesCount] = useState<number>(0);
    const [page, setPage] = useState<number>(initialListData?.page);
    const [pageSize, setPageSize] = useState<number>(initialListData?.pageSize);
    const [sort, setSort] = useState<string>(initialListData.sort);
    const [tableData, setTableData] = useState<RowDataType[]>([]);

    const [notificationList, { data }] = useLazyNotificationListQuery();

    const [statusActiveApi, { error, isError, isSuccess }] =
        useNotificationActivateMutation();

    const navigate = useNavigate();

    const dispatch = useAppDispatch();

    const getNotificationList = () => {
        notificationList({
            ...dataNotificationRequestList,
            sort: sort,
            page: page,
            pageSize: pageSize,
        });
    };

    const notificationDetails = (notificationId: NotificationDetailsEdit) => {
        navigate(
            `/notification-details-edit/${notificationId.notificationId}/${notificationId.statusRaw}`,
        );
    };

    const newNotificationDetails = () => {
        navigate('/notification-details');
    };

    const handleFilterData = (currentPage = 0) => {
        setPage(currentPage + 1);
    };

    const handelClickStatus = (notificationId: string) => {
        statusActiveApi(notificationId);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setPageSize(Number(event.target.value));
        setPage(1);
    };

    useEffect(() => {
        setPagesCount(
            data && data?.data.length > 0 ? Number(data?.rowsCount) : 0,
        );
        const tableData: RowDataType[] = data
            ? data.data.map((fullInfo) => {
                  return {
                      ...fullInfo,
                      statusRaw: fullInfo.notificationStatus,
                      status: (
                          <Box>
                              <Button
                                  variant="contained"
                                  size="small"
                                  sx={{
                                      bgcolor: getStatusColor(
                                          fullInfo.notificationStatus,
                                      ),
                                      pointerEvents:
                                          fullInfo.notificationStatus ===
                                          'draft'
                                              ? 'auto'
                                              : 'none',
                                      opacity:
                                          fullInfo.notificationStatus ===
                                          'draft'
                                              ? 1
                                              : 0.5,
                                  }}
                                  onClick={(event) => {
                                      event.stopPropagation();
                                      dispatch(
                                          openConfirmationDialogs({
                                              textInfo:
                                                  'Are You Sure Want To Active Notification',
                                              handleConfirm: () =>
                                                  handelClickStatus(
                                                      fullInfo.id,
                                                  ),
                                          }),
                                      );
                                  }}
                              >
                                  {fullInfo.notificationStatus}
                              </Button>
                          </Box>
                      ),
                      rowComponent: StyledTableRow,
                  };
              })
            : [];
        setTableData(tableData);
    }, [data]);

    useEffect(() => {
        if (dataNotificationRequestList) {
            getNotificationList();
        }
    }, [dataNotificationRequestList, page, pageSize, sort, isSuccess]);

    useEffect(() => {
        getNotificationList();
    }, []);

    useEffect(() => {
        if (error && 'data' in error && error.data?.message) {
            dispatch(
                openSnackbar({
                    message: error.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [isError]);

    return (
        <Box>
            <Filter>
                <FilterNotificationSystem
                    setDataNotificationRequestList={
                        setDataNotificationRequestList
                    }
                />
            </Filter>
            <Box
                bgcolor={colors.secondary.light}
                borderRadius={borders.borderRadius.md}
                mt={pxToRem(7)}
            >
                <FlexibleTable
                    tableMetaDataList={tableMetaDataList}
                    setTableMetaDataList={setTableMetaDataList}
                    tableDataList={tableData}
                    sort={sort}
                    setSort={setSort}
                    createNewFun={newNotificationDetails}
                    buttonName="Create New Notification"
                    rowClickFunction={(row) => {
                        notificationDetails({
                            notificationId: row?.id,
                            statusRaw: row?.statusRaw,
                        });
                    }}
                />
                {pagesCount > 0 && (
                    <Stack spacing={2} alignItems={'end'}>
                        <TablePagination
                            rowsPerPageOptions={[10, 30, 50]}
                            component="div"
                            count={pagesCount}
                            rowsPerPage={pageSize}
                            page={page - 1}
                            labelRowsPerPage={'Number of rows'}
                            slotProps={{
                                select: {
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                    MenuProps: {
                                        disablePortal: true,
                                        slotProps: {
                                            paper: {
                                                sx: {
                                                    backgroundColor:
                                                        colors.secondary.main,
                                                    padding: 0,
                                                },
                                            },
                                        },
                                    },
                                    native: false,
                                },
                            }}
                            onPageChange={(_, page) => handleFilterData(page)}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Stack>
                )}
            </Box>
        </Box>
    );
};

export default Notification;
